import { useEffect, useMemo, useRef, useState } from "react";
import { categoryRank, sectionRank, can, Permission } from "@ohc/shared";
import { ApiError, api, formatMoney, type SessionInfo } from "../api";
import { workingOf } from "../working";
import { useCoveredPhoto } from "../certCover";
import { ageOf } from "../dates";
import { SelectField } from "../fields";
import { go } from "../layout/router";
import { Alert, Badge, Empty, Loading, Modal } from "../ui";
import {
  type Catalog,
  type CatalogProduct,
  type CatalogSlab,
  type Checkout,
  dismissProblem,
  type LocalSale,
  loadCatalog,
  newId,
  type SaleLine,
  startQueue,
  tillAccount,
  useQueue,
} from "./engine";
import { PosCheckout } from "./PosCheckout";
import { PosCount } from "./PosCount";
import { PosAmount, PosNumber } from "./PosAmount";
import { PosBuy } from "./PosBuy";
import { PosSales } from "./PosSales";
import { PosStaff } from "./PosStaff";
import { OutgoingTransfers, usePendingTransfers } from "../OutgoingTransfers";
import { brandLogo } from "../brand";
import { productCode } from "../productCode";
import { productName } from "../productName";

/**
 * The booth screen (quotation C1 and C2), built for an iPad held by whoever is free.
 *
 * Big targets, one tap to add, swipe to move between categories and to take a line out
 * of the basket (answer 162). It sells from the catalog kept on the device, so a dead
 * signal in the hall changes nothing on this screen except the word at the top.
 */

const SETUP_KEY = "ohc.pos.setup";
/** Who was last at the till on this iPad, so a reload does not change the seller. */
const SELLER_KEY = "ohc.pos.seller";

function loadSeller(): string | undefined {
  try {
    return window.localStorage.getItem(SELLER_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

interface Setup {
  readonly locationId: string;
  readonly event: string;
}

function loadSetup(): Setup {
  try {
    const saved = JSON.parse(window.localStorage.getItem(SETUP_KEY) ?? "{}") as Partial<Setup>;
    return { locationId: saved.locationId ?? "", event: saved.event ?? "" };
  } catch {
    return { locationId: "", event: "" };
  }
}

/** A horizontal swipe on an element: left or right, past a threshold, and not a scroll. */
function useSwipe(onSwipe: (direction: "left" | "right") => void): {
  onPointerDown: (event: React.PointerEvent) => void;
  onPointerUp: (event: React.PointerEvent) => void;
} {
  const start = useRef<{ x: number; y: number } | null>(null);
  return {
    onPointerDown: (event) => {
      start.current = { x: event.clientX, y: event.clientY };
    },
    onPointerUp: (event) => {
      const from = start.current;
      start.current = null;
      if (!from) return;
      const dx = event.clientX - from.x;
      const dy = event.clientY - from.y;
      if (Math.abs(dx) > 70 && Math.abs(dx) > Math.abs(dy) * 1.5) onSwipe(dx < 0 ? "left" : "right");
    },
  };
}

/** A row of chips with All first, in the colour of what it narrows. */
function Chips({
  label,
  options,
  names,
  value,
  tone,
  onPick,
}: {
  readonly label: string;
  readonly options: readonly string[];
  readonly names?: Readonly<Record<string, string>>;
  readonly value: string;
  readonly tone: "category" | "language" | "section";
  readonly onPick: (value: string) => void;
}): React.ReactElement {
  const on = {
    category: "bg-primary/10 text-primary border-primary/30",
    language: "bg-warning/10 text-warning border-warning/30",
    section: "bg-info/10 text-info border-info/30",
  }[tone];
  return (
    <div className="flex gap-2 shrink-0" role="tablist" aria-label={label}>
      {["", ...options].map((option) => (
        <button
          key={option || "all"}
          type="button"
          role="tab"
          aria-selected={option === value}
          className={`whitespace-nowrap py-1 px-3 rounded-full text-[0.813rem] font-medium border ${
            option === value ? on : "border-defaultborder text-defaulttextcolor"
          }`}
          onClick={() => onPick(option)}
        >
          {option === "" ? "All" : (names?.[option] ?? option)}
        </button>
      ))}
    </div>
  );
}

/** The thing a shelf sells when a customer picks up something that is not on the list. */
/**
 * Where a tile goes on its shelf before anything else is looked at: what is pinned, then
 * what sells, then what the shop holds and does not sell, and the Unlisted item last, where
 * the hand expects the button for anything else.
 */
function placeOf(product: CatalogProduct): number {
  if (product.notForSale) return 2;
  if (product.pinned) return 0;
  return isUnlisted(product) ? 3 : 1;
}

function isUnlisted(product: CatalogProduct): boolean {
  return !product.tracksStock && product.priceCents === null && /^unlisted item$/i.test(product.name.trim());
}

/** A product's section, and Others for one nobody has put in a section yet. */
function sectionOf(product: CatalogProduct): string {
  return product.section?.trim() || "Others";
}

interface BasketLine extends SaleLine {
  readonly listPriceCents: number | null;
  readonly limit: number | null;
}

/** The games first, in the order the shop sells them. */
const GAME_ORDER = [/pok[eé]mon/i, /riftbound/i, /lorcana/i];

/** Whether this iPad shows what things cost. Off unless somebody turns it on. */
const SHOW_COST_KEY = "ohc.pos.showCost";

/** What the language codes on a product are called on a button. */
const LANGUAGE_NAMES: Record<string, string> = {
  EN: "English",
  JP: "Japanese",
  JA: "Japanese",
  CN: "Chinese",
  ZH: "Chinese",
  KR: "Korean",
  KO: "Korean",
};

export function Pos({ session, onSignOut }: { readonly session: SessionInfo; readonly onSignOut: () => void }): React.ReactElement {
  // Sales staff only sell: no counting, no day's takings, no cost, no transfers (D, 1 November 2026).
  const sellOnly = session.user.role === "sales_staff";
  const [catalog, setCatalog] = useState<Catalog | undefined>();
  const [loadError, setLoadError] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<"products" | "slabs">("products");
  const [game, setGame] = useState<string>("");
  const [category, setCategory] = useState<string>("");
  const [language, setLanguage] = useState<string>("");
  /** Packs, Bundles and so on inside the category; empty is the whole category. */
  const [section, setSection] = useState<string>("");
  /** The same narrowing for a search, kept apart so the shelf is as it was afterwards. */
  const [refine, setRefine] = useState({ category: "", language: "", section: "" });
  /** Photos from a basket line, opened large to check it is the right thing. */
  const [viewing, setViewing] = useState<{ title: string; urls: readonly string[] } | undefined>();
  /*
   * What things cost is for the shop, not for the customer on the other side of the
   * table, who can see this screen. So it is hidden unless somebody turns it on, and the
   * switch is one tap in the bar so it can be turned on for a moment and off again.
   * Remembered on this iPad, and nowhere else.
   */
  const [showCost, setShowCost] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(SHOW_COST_KEY) === "yes";
    } catch {
      return false;
    }
  });
  function toggleCost(): void {
    setShowCost((was) => {
      try {
        window.localStorage.setItem(SHOW_COST_KEY, was ? "no" : "yes");
      } catch {
        // A private window keeps nothing; the switch still works until the app closes.
      }
      return !was;
    });
  }
  const [search, setSearch] = useState("");
  const [basket, setBasket] = useState<BasketLine[]>([]);
  /** A sale being put right: its lines are in the basket, and this is what they replace. */
  const [correcting, setCorrecting] = useState<{ sale: LocalSale; by: { id: string; name: string } } | undefined>();

  /** A sale's lines back in the basket, as they were sold, to be changed and paid again. */
  function startCorrecting(sale: LocalSale, by: { id: string; name: string }): void {
    setShowSales(false);
    setDone(undefined);
    setCorrecting({ sale, by });
    setBasket(
      sale.lines.map((line) => {
        const product = catalog?.products.find((one) => one.id === line.productId);
        const typed = product && !product.tracksStock && product.priceCents === null;
        return {
          ...line,
          lineId: newId(),
          listPriceCents: product?.priceCents ?? null,
          // A slab and an amount typed at the table are one of a kind; the rest can change.
          limit: line.slabLotId || typed ? 1 : null,
        };
      }),
    );
  }
  const [setup, setSetup] = useState<Setup>(loadSetup);
  const [checkout, setCheckout] = useState<"cash" | "paynow" | "both" | undefined>();
  /**
   * Keying in sales for an earlier day from its photos (D, 10 October 2026: "go back to 19
   * July on the POS and pretend I am doing the tradeshow"). Each sale moves the time on a
   * minute, so the day's sales stay in the order they were keyed.
   */
  const [earlier, setEarlier] = useState<{ day: string; time: string } | undefined>();
  const [pickingEarlier, setPickingEarlier] = useState(false);
  /** The number the basket was given when payment was first opened, kept through Back. */
  const [reserved, setReserved] = useState<Checkout | undefined>();
  useEffect(() => {
    if (basket.length === 0) setReserved(undefined);
  }, [basket.length]);
  const [showSales, setShowSales] = useState(false);
  const [buying, setBuying] = useState(false);
  const [counting, setCounting] = useState(false);
  const [done, setDone] = useState<string | undefined>();
  /** Something that did not work, such as pinning with no connection. */
  const [trouble, setTrouble] = useState<string | undefined>();
  /** The setting-up warning, put away until the POS is next opened. */
  const [setupHidden, setSetupHidden] = useState(false);
  /** True while the stock list is being fetched again by hand. */
  const [fetching, setFetching] = useState(false);
  /** The uncounted product whose amount is being typed, if any. */
  const [typing, setTyping] = useState<CatalogProduct | undefined>();
  const [sellerId, setSellerId] = useState<string>(() => loadSeller() ?? session.user.userId);
  const [switching, setSwitching] = useState(false);
  const queue = useQueue();
  /** Transfers out the bank reported that nobody has explained yet (Daren, 14 October 2026). */
  const waitingTransfers = usePendingTransfers(!sellOnly);
  const [explaining, setExplaining] = useState(false);

  async function refresh(): Promise<void> {
    const result = await loadCatalog();
    if (result.catalog) setCatalog(result.catalog);
    setLoadError(result.catalog ? undefined : (result.error ?? "The product catalogue is not on this iPad yet. Connect to the internet once to load it"));
    setLoading(false);
  }

  useEffect(() => {
    void refresh();
    const stop = startQueue();
    // Prices and stock move during the day: take a fresh copy every five minutes.
    const timer = window.setInterval(() => void refresh(), 5 * 60_000);
    return () => {
      stop();
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(SETUP_KEY, JSON.stringify(setup));
    } catch {
      // A private window keeps nothing; the setup is asked for again next time.
    }
  }, [setup]);

  // What a customer asks for, in order: the game, then the shape of the thing.
  //
  // A product added in a hurry may have no game against it. It still has to be reachable
  // at the booth, so those sit under Other rather than behind no chip at all.
  const OTHER = "Other";
  // A game written as Other is no game at all (D, 19 October 2026: the product list said Other for
  // Gatchapon and Lucky Pull, and they vanished from the iPad).
  const noGame = (name: string | null | undefined): boolean => !name || /^(other|others|none|n\/a|-)$/i.test(name.trim());
  const games = useMemo(() => {
    // The order the shop sells in, which is D's: Pokémon, Riftbound, Lorcana, then the
    // rest by name, and Other last. Matched loosely, because a sheet writes Pokemon or
    // Pokémon and Lorcana or Disney Lorcana.
    const rank = (name: string): number => {
      const at = GAME_ORDER.findIndex((pattern) => pattern.test(name));
      return at === -1 ? GAME_ORDER.length : at;
    };
    const named = [
      ...new Set((catalog?.products ?? []).map((product) => product.game).filter((name): name is string => !noGame(name))),
    ].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
    const anyWithout = (catalog?.products ?? []).some((product) => noGame(product.game));
    return anyWithout && named.length > 0 ? [...named, OTHER] : named;
  }, [catalog]);
  const currentGame = game && games.includes(game) ? game : (games[0] ?? "");
  const inGame = useMemo(
    () =>
      (catalog?.products ?? []).filter((product) =>
        games.length === 0 ? true : currentGame === OTHER ? noGame(product.game) : product.game === currentGame,
      ),
    [catalog, games.length, currentGame],
  );
  // Sealed first, then singles and slabs, then the rest by name: D wanted Sealed Products
  // ahead of Accessories on the Lorcana shelf, and the booth sells sealed most.
  const categories = useMemo(
    () =>
      [...new Set(inGame.map((product) => product.category))].sort(
        (a, b) => categoryRank(a) - categoryRank(b) || a.localeCompare(b),
      ),
    [inGame],
  );
  const current = category && categories.includes(category) ? category : (categories[0] ?? "");
  const swipe = useSwipe((direction) => {
    const at = categories.indexOf(current);
    const next = categories[at + (direction === "left" ? 1 : -1)];
    if (next) {
      setCategory(next);
      setSection("");
    }
  });

  // A shelf of booster packs is three shelves once English, Japanese and Chinese are on
  // it, so the language is a third row of chips. It is only there when the category
  // actually holds more than one, which is most of the time only the packs.
  const inCategory = useMemo(() => inGame.filter((product) => product.category === current), [inGame, current]);
  const languages = useMemo(
    () => [...new Set(inCategory.map((product) => (product.language ?? "").toUpperCase()).filter(Boolean))],
    [inCategory],
  );
  /** Something to narrow: two languages, or one and everything that has none. */
  const worthNarrowing =
    languages.length > 1 || (languages.length === 1 && inCategory.some((product) => !product.language));
  const currentLanguage = language && languages.includes(language) ? language : "";

  /*
   * Inside a category, the sections D sorted the sealed shelf into: Packs, Bundles, Booster
   * Boxes and so on, one tap to narrow a hundred tiles to thirty. A product nobody has put
   * in one goes under Others, and the Unlisted item, which is how anything not on the list
   * is sold, is in every one of them, at the end.
   */
  const onShelf = inCategory.filter(
    (product) => !currentLanguage || (product.language ?? "").toUpperCase() === currentLanguage,
  );
  const sections = [
    ...new Set(onShelf.filter((product) => !isUnlisted(product)).map((product) => sectionOf(product))),
  ].sort((a, b) => sectionRank(a) - sectionRank(b) || a.localeCompare(b));
  const sectioned = onShelf.some((product) => product.section) && sections.length > 1;
  const currentSection = sectioned && section && sections.includes(section) ? section : "";

  const term = search.trim().toLowerCase();
  /*
   * A search looks through everything, and then narrows the way a shelf does: D asked for
   * the categories, languages and sections while searching, to pick the Japanese ones or
   * the packs out of everything called 151. The chips are made from what was found.
   */
  // Everything about a product, and the words in any order (D, 12 October 2026: "include
  // everything... I searched number but it does not appear"): its code, name, set, game,
  // category, subcategory, language and its price, so "38" finds what sells at 38.00.
  const words = term.replace(/^s?\$/, "").split(/\s+/).filter(Boolean);
  const found = term
    ? (catalog?.products ?? []).filter((product) => {
        const about = [
          productCode(product),
          productName(product),
          product.name,
          product.setName,
          product.game,
          product.category,
          product.section,
          product.language,
          product.priceCents === null || product.priceCents === undefined ? "" : (product.priceCents / 100).toFixed(2),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return words.every((word) => about.includes(word));
      })
    : [];
  const foundCategories = [...new Set(found.map((product) => product.category))].sort(
    (a, b) => categoryRank(a) - categoryRank(b) || a.localeCompare(b),
  );
  const pickedCategory = foundCategories.includes(refine.category) ? refine.category : "";
  const inFoundCategory = found.filter((product) => !pickedCategory || product.category === pickedCategory);
  const foundLanguages = [
    ...new Set(inFoundCategory.map((product) => (product.language ?? "").toUpperCase()).filter(Boolean)),
  ];
  const pickedLanguage = foundLanguages.includes(refine.language) ? refine.language : "";
  const inFoundLanguage = inFoundCategory.filter(
    (product) => !pickedLanguage || (product.language ?? "").toUpperCase() === pickedLanguage,
  );
  const foundSections = [
    ...new Set(inFoundLanguage.filter((product) => !isUnlisted(product)).map((product) => sectionOf(product))),
  ].sort((a, b) => sectionRank(a) - sectionRank(b) || a.localeCompare(b));
  const pickedSection = foundSections.includes(refine.section) ? refine.section : "";
  // What sells goes to the top of its shelf by itself, and what is pinned goes above that.
  // The sort is stable, so things that sell the same stay in the order of their names.
  const products = [
    ...(term
      ? inFoundLanguage.filter((product) => !pickedSection || sectionOf(product) === pickedSection)
      : onShelf.filter(
          (product) => !currentSection || isUnlisted(product) || sectionOf(product) === currentSection,
        )),
  ].sort(
    (a, b) =>
      placeOf(a) - placeOf(b) ||
      // The whole category still reads in D's order, a section at a time.
      (!term && sectioned && !currentSection ? sectionRank(sectionOf(a)) - sectionRank(sectionOf(b)) : 0) ||
      (b.soldLately ?? 0) - (a.soldLately ?? 0),
  );
  // On the Slabs tab, the way to sell a slab nobody has entered yet: the game's own Slab,
  // sold by typing its amount and photographing it.
  const slabSellers = (catalog?.products ?? []).filter(
    (product) =>
      /^slabs?$/i.test(product.category) &&
      !product.tracksStock &&
      product.priceCents === null &&
      (games.length === 0 || (currentGame === OTHER ? noGame(product.game) : product.game === currentGame)),
  );
  const slabs = (catalog?.slabs ?? []).filter((slab) =>
    term ? `${slab.certNo ?? ""} ${slab.cardName ?? ""} ${slab.setName ?? ""}`.toLowerCase().includes(term) : true,
  );

  // Whoever entered their PIN last; the person the iPad is signed in as until then, or if
  // the one chosen has since left the staff list.
  const member = catalog?.staff.find((person) => person.id === sellerId);
  const seller = member ? { id: member.id, name: member.name } : { id: session.user.userId, name: session.user.fullName };

  function chooseSeller(id: string): void {
    setSellerId(id);
    setSwitching(false);
    setDone(undefined);
    try {
      window.localStorage.setItem(SELLER_KEY, id);
    } catch {
      // A private window keeps nothing: after a reload the signed-in person sells.
    }
  }

  async function reloadCatalog(): Promise<Catalog | undefined> {
    const result = await loadCatalog();
    if (result.catalog) setCatalog(result.catalog);
    return result.catalog;
  }

  /**
   * Fetch the stock list again, by hand.
   *
   * The iPad keeps the list so it can sell with no signal, and picks up changes when the
   * booth screen is opened. A product added at the desk during a show should not mean
   * leaving the screen and coming back, so this is here beside the search box.
   */
  async function refreshCatalog(): Promise<void> {
    setFetching(true);
    try {
      const fresh = await reloadCatalog();
      if (fresh) setDone("Products are up to date.");
    } finally {
      setFetching(false);
    }
  }
  /**
   * Pin a product to the top of its shelf, or let it go, from the till itself: D asked for
   * the salespeople to be able to. It shows at once and is kept on the server, so every
   * iPad has it after its next refresh; with no connection it is put back and said so.
   */
  async function togglePin(product: CatalogProduct): Promise<void> {
    const pinned = !product.pinned;
    const mark = (value: boolean): void =>
      setCatalog((current) =>
        current
          ? { ...current, products: current.products.map((one) => (one.id === product.id ? { ...one, pinned: value } : one)) }
          : current,
      );
    mark(pinned);
    setTrouble(undefined);
    try {
      await api.pinProduct(product.id, pinned);
      setDone(pinned ? `${productName(product)} is pinned to the top of its product group.` : `${productName(product)} is no longer pinned.`);
      await reloadCatalog();
    } catch (problem) {
      mark(!pinned);
      setDone(undefined);
      setTrouble(
        problem instanceof ApiError && problem.status !== 0
          ? problem.message
          : "Pinning requires an internet connection. Try again when the iPad is online.",
      );
    }
  }

  /*
   * Holding a tile for a moment pins it; a tap still sells it. A finger that moves is a
   * scroll or a swipe, not a hold, and the tap that ends a hold is swallowed so the product
   * does not also go into the basket. A right click does the same on a laptop.
   */
  const hold = useRef<{ timer: number; x: number; y: number } | undefined>(undefined);
  const held = useRef(false);
  function endHold(): void {
    if (hold.current) window.clearTimeout(hold.current.timer);
    hold.current = undefined;
  }
  function holdable(product: CatalogProduct): React.ButtonHTMLAttributes<HTMLButtonElement> {
    return {
      onPointerDown: (event) => {
        held.current = false;
        endHold();
        if (event.button !== 0) return;
        const timer = window.setTimeout(() => {
          hold.current = undefined;
          held.current = true;
          void togglePin(product);
        }, 600);
        hold.current = { timer, x: event.clientX, y: event.clientY };
      },
      onPointerMove: (event) => {
        if (hold.current && Math.hypot(event.clientX - hold.current.x, event.clientY - hold.current.y) > 10) endHold();
      },
      onPointerUp: endHold,
      onPointerLeave: endHold,
      onPointerCancel: endHold,
      onContextMenu: (event) => {
        event.preventDefault();
        if (held.current) return;
        endHold();
        held.current = true;
        void togglePin(product);
      },
      onClick: () => {
        if (held.current) {
          held.current = false;
          return;
        }
        addProduct(product);
      },
    };
  }

  const total = basket.reduce((sum, line) => sum + line.quantity * line.unitPriceCents, 0);
  const inBasket = (productId: string): number =>
    basket.filter((line) => line.productId === productId).reduce((sum, line) => sum + line.quantity, 0);

  /** The picture of the thing on a line, if it has one. */
  function pictureOf(line: BasketLine): string | null {
    // A graded card shows its own photo (D, 12 October 2026: "for slabs, allow slabs to show slabs image").
    if (line.slabLotId) return catalog?.slabs.find((one) => one.lotId === line.slabLotId)?.photoId ?? null;
    return catalog?.products.find((one) => one.id === line.productId)?.photoId ?? null;
  }

  /** What the shop pays for the thing on a line, and the sum behind it. */
  function floorOf(line: BasketLine): { cents: number; note: string | null } | null {
    const product = catalog?.products.find((one) => one.id === line.productId);
    if (!product?.costCents) return null;
    return { cents: product.costCents, note: product.costNote ?? null };
  }

  function addProduct(product: CatalogProduct): void {
    // Held, not sold: the shop keeps it and the booth says so rather than selling it.
    if (product.notForSale) {
      setTrouble(`${productName(product)} is not available for sale.`);
      return;
    }
    setTrouble(undefined);
    setDone(undefined);
    // Raw singles and the like: what the customer agreed is typed in, and the same thing
    // can be added twice at two different amounts, so each tap is its own line.
    if (!product.tracksStock && product.priceCents === null) {
      setTyping(product);
      return;
    }
    setBasket((lines) => {
      const existing = lines.find((line) => line.productId === product.id);
      if (existing) {
        return lines.map((line) => (line === existing ? { ...line, quantity: line.quantity + 1 } : line));
      }
      return [
        ...lines,
        {
          lineId: newId(),
          productId: product.id,
          quantity: 1,
          unitPriceCents: product.priceCents ?? 0,
          listPriceCents: product.priceCents,
          description: productName(product),
          limit: null,
        },
      ];
    });
  }

  /** An amount someone typed at the table, added as its own line. */
  function addTyped(
    product: CatalogProduct,
    cents: number,
    count: number,
    said: string,
    photos: readonly { contentType: string; data: string }[],
  ): void {
    setTyping(undefined);
    setDone(undefined);
    // What the desk will see beside the amount: how many there were and what they were.
    // Cut to what the server takes, here rather than there: a line it will not read is a
    // line that stops everything behind it from being sent.
    const note = [count > 1 ? `${count} items` : null, said || null].filter(Boolean).join(": ").slice(0, 120);
    setBasket((lines) => [
      ...lines,
      {
        lineId: newId(),
        productId: product.id,
        // One line for the lot: the amount is the record, and it has to stay exact, which
        // a per-card price of thirty seven dollars over seven cards could not.
        quantity: 1,
        unitPriceCents: cents,
        listPriceCents: null,
        description: note ? `${productName(product)} (${note})` : productName(product),
        ...(note ? { note } : {}),
        ...(photos.length > 0 ? { photos: photos.map(({ contentType, data }) => ({ contentType, data })) } : {}),
        limit: 1,
      },
    ]);
  }

  function addSlab(slab: CatalogSlab): void {
    setDone(undefined);
    if (basket.some((line) => line.slabLotId === slab.lotId)) return;
    setBasket((lines) => [
      ...lines,
      {
        lineId: newId(),
        slabLotId: slab.lotId,
        quantity: 1,
        unitPriceCents: slab.priceCents ?? 0,
        listPriceCents: slab.priceCents,
        description: [slab.cardName ?? "Graded card", slab.grader && slab.grade ? `${slab.grader} ${slab.grade}` : null]
          .filter(Boolean)
          .join(" "),
        limit: 1,
      },
    ]);
  }

  function changeLine(lineId: string, change: Partial<Pick<BasketLine, "quantity" | "unitPriceCents" | "note" | "description">>): void {
    setBasket((lines) =>
      lines.flatMap((line) => {
        if (line.lineId !== lineId) return [line];
        const next = { ...line, ...change };
        return next.quantity <= 0 ? [] : [next];
      }),
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bodybg">
        <Loading />
      </div>
    );
  }

  if (!catalog) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bodybg p-6">
        <div className="box max-w-lg w-full">
          <div className="box-body text-center">
            <Empty icon="ri-tablet-line">{loadError}</Empty>
            {session.device ? null : <SetUpHere onDone={() => void refresh()} />}
            <button type="button" className="ti-btn ti-btn-light mt-2" onClick={sellOnly ? onSignOut : () => go("home")}>
              {sellOnly ? "Sign out" : "Back to dashboard"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const canSetAccounts = session.user.role === "admin" || session.user.role === "master";
  /*
   * Which account the money goes into only needs saying when there is a choice. With one
   * cash account there is nothing to ask; with two bank accounts somebody has to say which
   * one PayNow lands in, once, and until then a sale paid later cannot be settled.
   */
  const accountsMissing = !tillAccount(catalog, "cash") || !tillAccount(catalog, "paynow");
  const accountWord = (kind: "cash" | "bank"): string =>
    catalog.accounts.some((account) => account.kind === kind)
      ? `set the ${kind === "cash" ? "cash" : "PayNow"} account`
      : `add a company ${kind} account on the dashboard`;
  // What has not been set up yet, in the order somebody would hit it.
  const notReady = [
    setup.locationId ? null : "select a sales location",
    tillAccount(catalog, "cash") ? null : accountWord("cash"),
    tillAccount(catalog, "paynow") ? null : accountWord("bank"),
    catalog.products.length + catalog.slabs.length > 0 ? null : "refresh products",
  ].filter((thing): thing is string => thing !== null);

  const shows = catalog.locations;
  const standing = shows.find((place) => place.id === setup.locationId);
  /*
   * At the shop there is no show to name: every sale already carries its date, and D did
   * not want the staff changing a date by hand each morning. So the shop's day is today's,
   * shown in the bar and moving on by itself, and nothing typed goes on its sales.
   */
  const atShop = standing?.kind === "shop";
  const eventNow = atShop ? null : setup.event.trim() || null;
  const todayWords = new Intl.DateTimeFormat("en-SG", { weekday: "short", day: "numeric", month: "short", timeZone: "Asia/Singapore" }).format(new Date());
  const transactions = `${queue.waiting} ${queue.waiting === 1 ? "transaction" : "transactions"}`;
  const status = !queue.online
    ? {
        tone: "warning" as const,
        text: queue.waiting ? `Offline - ${transactions} waiting to upload` : "Offline - sales saved on this iPad",
      }
    : queue.waiting
      ? { tone: "info" as const, text: queue.sending ? `Uploading ${transactions}` : `${transactions} waiting to upload` }
      : { tone: "success" as const, text: "Online - all transactions saved" };

  return (
    <div className="h-screen flex flex-col bg-bodybg select-none pb-[env(safe-area-inset-bottom)]">
      {/*
        The bar across the top: which iPad, where it is standing, who is selling, and
        whether everything is sent. It has to hold one line on an iPad held upright, which
        is 810 points across, so the only thing here allowed to grow is the booth, and the
        booth is a line of words rather than two boxes: where the iPad is standing and what
        the day is called are set once in the morning under Today's sales, and read all day
        here. D's longer labels (4 October) are set a step smaller; where they still do not
        fit, the bar wraps rather than squeezing the booth to nothing.
      */}
      <header className="bg-white dark:bg-bodybg2 shadow-sm flex flex-wrap items-center gap-x-3 gap-y-2 px-4 min-h-[4rem] shrink-0">
        <img src={brandLogo("emblem")} alt="Oh-Ho Collectibles" className="h-11 w-11 shrink-0" />
        {/*
          Which till this is. The letter goes inside every reference this iPad writes, so it
          is worth a glance, and on a tablet it is all there is room for. The name is written
          out where there is room, and at the head of Today wherever there is not.
        */}
        <span className="badge bg-primary/10 text-primary font-semibold !mb-0 shrink-0 xl:hidden">
          {catalog.device.code}
        </span>
        <div className="leading-tight min-w-0 max-w-[9rem] hidden xl:block shrink-0">
          <div className="font-semibold text-[0.938rem] truncate">{catalog.device.name}</div>
          <div className="text-[0.75rem] text-[#8c9097] dark:text-white/50">Device {catalog.device.code}</div>
        </div>
        <button
          type="button"
          aria-label="Where this iPad is standing"
          title="Sales location and event"
          className="flex-1 min-w-[6.5rem] text-start leading-tight"
          onClick={() => setShowSales(true)}
        >
          <div className={`text-[0.875rem] font-semibold truncate ${standing ? "" : "text-warning"}`}>
            {standing?.name ?? "Select sales location"}
          </div>
          <div className="text-[0.75rem] text-[#8c9097] dark:text-white/50 truncate">
            {atShop ? todayWords : setup.event.trim() || (sellOnly ? "Tap to set the event" : "Set under Today's sales")}
          </div>
        </button>
        <span className="whitespace-nowrap hidden md:inline">
          <Badge tone={status.tone}>{status.text}</Badge>
        </span>
        {sellOnly ? null : (
        <button
          type="button"
          aria-label={showCost ? "Hide what things cost" : "Show what things cost"}
          aria-pressed={showCost}
          title={showCost ? "Hide cost details" : "Show cost details"}
          className={`ti-btn ti-btn-icon !mb-0 ${showCost ? "ti-btn-warning-full" : "ti-btn-light"}`}
          onClick={toggleCost}
        >
          <i className={showCost ? "ri-eye-line" : "ri-eye-off-line"} />
        </button>
        )}
        <button type="button" className="ti-btn ti-btn-light !mb-0 !px-2 !text-[0.75rem] whitespace-nowrap" onClick={() => setBuying(true)}>
          <i className="ri-hand-coin-line" /> Buy from customer
        </button>
        {sellOnly ? null : (
          <>
            <button type="button" className="ti-btn ti-btn-light !mb-0 !px-2 !text-[0.75rem] whitespace-nowrap" onClick={() => setCounting(true)}>
              <i className="ri-list-check-2" /> Count inventory
            </button>
            <button type="button" className="ti-btn ti-btn-light !mb-0 !px-2 !text-[0.75rem] whitespace-nowrap" onClick={() => setShowSales(true)}>
              <i className="ri-file-list-3-line" /> Today's sales
            </button>
          </>
        )}
        {canSetAccounts ? (
          <button
            type="button"
            className={`ti-btn ${earlier ? "ti-btn-warning-full" : "ti-btn-light"} !mb-0 !px-2 !text-[0.75rem] whitespace-nowrap`}
            onClick={() => setPickingEarlier(true)}
          >
            <i className="ri-history-line" /> {earlier ? "Past day on" : "Key in a past day"}
          </button>
        ) : null}
        <button
          type="button"
          aria-label="Switch who is selling"
          title="Switch salesperson"
          className="flex items-center gap-2 ps-3 border-s border-defaultborder text-start"
          onClick={() => setSwitching(true)}
        >
          <span className="avatar avatar-sm avatar-rounded bg-primary !text-white font-semibold !mb-0">
            {seller.name
              .split(/\s+/)
              .map((word) => word.charAt(0))
              .join("")
              .slice(0, 2)
              .toUpperCase()}
          </span>
          <div className="leading-tight whitespace-nowrap hidden lg:block">
            <div className="text-[0.813rem] font-semibold">{seller.name}</div>
            <div className="text-[0.6875rem] text-[#8c9097] dark:text-white/50">
              <i className="ri-arrow-left-right-line" /> Switch salesperson
            </div>
          </div>
        </button>
        {sellOnly ? (
          <button type="button" aria-label="Sign out" title="Sign out" className="ti-btn ti-btn-icon ti-btn-light !mb-0" onClick={onSignOut}>
            <i className="ri-logout-box-r-line text-[1.25rem]" />
          </button>
        ) : (
          <button
            type="button"
            aria-label="Back to the desk"
            title="Back to dashboard"
            className="ti-btn ti-btn-icon ti-btn-light !mb-0"
            onClick={() => go("home")}
          >
            <i className="ri-close-line text-[1.25rem]" />
          </button>
        )}
      </header>

      {queue.problem ? (
        <div className="px-4 pt-3">
          <Alert tone="danger" onClose={dismissProblem}>
            {queue.problem}
          </Alert>
        </div>
      ) : null}

      {/*
        Said here rather than at the checkout. A till that only mentions a missing setting
        when somebody is standing there with money in their hand is a till that embarrasses
        whoever is holding it.
      */}
      {earlier ? (
        <div className="px-4 pt-3">
          <Alert tone="warning">
            Keying in sales for{" "}
            <span className="font-semibold">
              {new Date(`${earlier.day}T12:00:00+08:00`).toLocaleDateString("en-SG", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </span>
            , next at {earlier.time}. Each sale goes straight to the books on that day; afterwards match the PayNow ones in Bank
            reconciliation.
            <button type="button" className="ti-btn ti-btn-light !py-1 !px-2 !text-[0.75rem] !mb-0 ms-2" onClick={() => setEarlier(undefined)}>
              Back to today
            </button>
          </Alert>
        </div>
      ) : null}
      {pickingEarlier ? (
        <EarlierDayPicker
          start={earlier}
          onClose={() => setPickingEarlier(false)}
          onPick={(picked) => {
            setEarlier(picked);
            setPickingEarlier(false);
          }}
        />
      ) : null}
      {!sellOnly && waitingTransfers.transfers.length > 0 ? (
        <div className="px-4 pt-3">
          <Alert tone="danger">
            {waitingTransfers.transfers.length} {waitingTransfers.transfers.length === 1 ? "transfer" : "transfers"} out of the OCBC account
            {waitingTransfers.transfers.length === 1 ? " needs" : " need"} a description: what the money was for.
            <button type="button" className="ti-btn ti-btn-light !py-1 !px-2 !text-[0.75rem] !mb-0 ms-2" onClick={() => setExplaining(true)}>
              Explain now
            </button>
          </Alert>
        </div>
      ) : null}
      {explaining ? (
        <OutgoingTransfers
          transfers={waitingTransfers.transfers}
          onClose={() => {
            setExplaining(false);
            waitingTransfers.reload();
          }}
          onChanged={() => undefined}
        />
      ) : null}
      {notReady.length > 0 && !setupHidden ? (
        <div className="px-4 pt-3">
          <Alert tone="warning" onClose={() => setSetupHidden(true)}>
            Before the first sale: {notReady.join(", ")}.
            {accountsMissing && !canSetAccounts
              ? " Ask a system administrator to configure the iPad's payment accounts on the dashboard, under Devices."
              : ""}
            {!setup.locationId || (canSetAccounts && accountsMissing) ? (
              <button
                type="button"
                className="ti-btn ti-btn-light !py-1 !px-2 !text-[0.75rem] !mb-0 ms-2"
                onClick={() => setShowSales(true)}
              >
                Set them now
              </button>
            ) : null}
          </Alert>
        </div>
      ) : null}

      <main className="flex-1 grid grid-cols-12 gap-4 p-4 min-h-0">
        {/* What is for sale. */}
        <section className="col-span-12 lg:col-span-8 box !mb-0 flex flex-col min-h-0">
          <div className="box-header !py-3 flex-wrap gap-3">
            <nav className="flex gap-2" role="tablist" aria-label="What to sell">
              {(["products", "slabs"] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  role="tab"
                  aria-selected={tab === value}
                  className={`py-2 px-4 text-[0.938rem] font-semibold rounded-md ${tab === value ? "bg-primary text-white" : "bg-light text-defaulttextcolor"}`}
                  onClick={() => setTab(value)}
                >
                  {value === "products" ? "Products" : `Graded cards (${catalog.slabs.length})`}
                </button>
              ))}
            </nav>
            <div className="input-group flex-1 min-w-[14rem]">
              <span className="input-group-text">
                <i className="ri-search-line" />
              </span>
              <input
                id="pos-search"
                type="search"
                className="form-control"
                value={search}
                placeholder={tab === "slabs" ? "Search by certificate number or card name" : "Search by product code, name or set"}
                aria-label="Search"
                onChange={(event) => {
                  setSearch(event.target.value);
                  setRefine({ category: "", language: "", section: "" });
                }}
              />
              {/* All of it at once, not letter by letter (D, 18 October 2026). */}
              {search ? (
                <button
                  type="button"
                  aria-label="Clear the search"
                  className="input-group-text !bg-white dark:!bg-bodybg !px-3"
                  onClick={() => setSearch("")}
                >
                  <i className="ri-close-line text-[1.125rem]" />
                </button>
              ) : null}
            </div>
            {/* A product added at the desk while the booth is open. Without this the iPad
                only picks it up when the screen is left and opened again. */}
            <button
              type="button"
              aria-label="Fetch the newest stock list"
              title="Refresh products"
              className="ti-btn ti-btn-light !mb-0 shrink-0"
              disabled={fetching}
              onClick={() => void refreshCatalog()}
            >
              <i className={`ri-refresh-line text-[1.125rem] ${fetching ? "animate-spin" : ""}`} />
            </button>
          </div>

          {/*
            The rows of chips are kept low and the language sits on the category's row, so
            the room goes to the products: D asked for four or five rows of them on screen.
          */}
          {!term && games.length > 1 ? (
            <div className="flex gap-2 overflow-x-auto px-4 pt-2 shrink-0" role="tablist" aria-label="Games">
              {games.map((name) => (
                <button
                  key={name}
                  type="button"
                  role="tab"
                  aria-selected={name === currentGame}
                  className={`whitespace-nowrap py-1.5 px-4 rounded-full text-[0.875rem] font-semibold border ${
                    name === currentGame
                      ? "bg-primary text-white border-primary"
                      : "border-defaultborder text-defaulttextcolor"
                  }`}
                  onClick={() => {
                    setGame(name);
                    setCategory("");
                    setLanguage("");
                    setSection("");
                  }}
                >
                  {name}
                </button>
              ))}
            </div>
          ) : null}

          {tab === "products" && !term ? (
            <div className="flex items-center gap-2 overflow-x-auto px-4 pt-2 shrink-0">
              <div className="flex gap-2 shrink-0" role="tablist" aria-label="Categories">
                {categories.map((name) => (
                  <button
                    key={name}
                    type="button"
                    role="tab"
                    aria-selected={name === current}
                    className={`whitespace-nowrap py-1 px-3 rounded-full text-[0.813rem] font-medium border ${
                      name === current ? "bg-primary/10 text-primary border-primary/30" : "border-defaultborder text-defaulttextcolor"
                    }`}
                    onClick={() => {
                      setCategory(name);
                      setLanguage("");
                      setSection("");
                    }}
                  >
                    {name}
                  </button>
                ))}
              </div>
              {worthNarrowing ? (
                <>
                  <span className="w-px h-5 bg-defaultborder shrink-0" aria-hidden="true" />
                  <div className="flex gap-2 shrink-0" role="tablist" aria-label="Languages">
                    {["", ...languages].map((code) => (
                      <button
                        key={code || "all"}
                        type="button"
                        role="tab"
                        aria-selected={code === currentLanguage}
                        className={`whitespace-nowrap py-1 px-3 rounded-full text-[0.813rem] font-medium border ${
                          code === currentLanguage
                            ? "bg-warning/10 text-warning border-warning/30"
                            : "border-defaultborder text-defaulttextcolor"
                        }`}
                        onClick={() => setLanguage(code)}
                      >
                        {code === "" ? "All" : (LANGUAGE_NAMES[code] ?? code)}
                      </button>
                    ))}
                  </div>
                </>
              ) : null}
            </div>
          ) : null}

          {tab === "products" && term && found.length > 0 ? (
            <div className="px-4 pt-2 shrink-0 flex flex-col gap-2" aria-label="Narrow the search">
              <div className="flex items-center gap-2 overflow-x-auto">
                <Chips
                  label="Categories found"
                  options={foundCategories}
                  value={pickedCategory}
                  tone="category"
                  onPick={(category) => setRefine({ category, language: "", section: "" })}
                />
                {foundLanguages.length > 1 ? (
                  <>
                    <span className="w-px h-5 bg-defaultborder shrink-0" aria-hidden="true" />
                    <Chips
                      label="Languages found"
                      options={foundLanguages}
                      names={LANGUAGE_NAMES}
                      value={pickedLanguage}
                      tone="language"
                      onPick={(language) => setRefine({ ...refine, language, section: "" })}
                    />
                  </>
                ) : null}
              </div>
              {foundSections.length > 1 ? (
                <div className="flex items-center gap-2 overflow-x-auto">
                  <Chips
                    label="Sections found"
                    options={foundSections}
                    value={pickedSection}
                    tone="section"
                    onPick={(picked) => setRefine({ ...refine, section: picked })}
                  />
                </div>
              ) : null}
            </div>
          ) : null}

          {tab === "products" && !term && sectioned ? (
            <div className="flex gap-1.5 overflow-x-auto px-4 pt-2 shrink-0" role="tablist" aria-label="Sections">
              {["", ...sections].map((name) => (
                <button
                  key={name || "all"}
                  type="button"
                  role="tab"
                  aria-selected={name === currentSection}
                  className={`whitespace-nowrap py-1 px-2.5 rounded-full text-[0.813rem] font-medium border ${
                    name === currentSection
                      ? "bg-info/10 text-info border-info/30"
                      : "border-defaultborder text-defaulttextcolor"
                  }`}
                  onClick={() => setSection(name)}
                >
                  {name || "All"}
                </button>
              ))}
            </div>
          ) : null}

          <div className="box-body !p-3 flex-1 overflow-y-auto touch-pan-y" {...(tab === "products" && !term ? swipe : {})}>
            {tab === "products" ? (
              products.length === 0 ? (
                <Empty icon="ri-price-tag-3-line">{term ? "No products match your search." : "No products in this category."}</Empty>
              ) : (
                /*
                  Five across on an iPad either way up, and small enough for four or five rows,
                  which is what D asked for. A tile carries what is needed to sell from it: the
                  name, the language when it is not English, the price, and how many are left
                  when they are counted. When the price was set lives on the desk.
                */
                <div className="grid grid-cols-3 md:grid-cols-5 xxl:grid-cols-6 gap-2">
                  {products.map((product) => {
                    const left = product.onHand - inBasket(product.id);
                    // "Not available for sale" and "Complimentary item" are longer than a
                    // price: they may take two lines, and what sits beside them moves under.
                    const worded = product.notForSale || product.priceCents === 0;
                    return (
                      <button
                        key={product.id}
                        type="button"
                        title={productName(product)}
                        className={`ti-btn ti-btn-light !m-0 !p-2 !h-auto !flex !flex-col !items-stretch !justify-start !gap-0 text-start !rounded-md min-h-[4.75rem] relative [-webkit-touch-callout:none]${
                          product.notForSale ? " opacity-60" : ""
                        }`}
                        aria-disabled={product.notForSale ? true : undefined}
                        {...holdable(product)}
                      >
                        {product.pinned ? (
                          <i className="ri-pushpin-fill text-primary absolute top-1 end-1 text-[0.625rem]" aria-label="Pinned" />
                        ) : null}
                        <div className="flex items-start gap-1.5">
                          {product.photoId ? (
                            <img
                              src={`/api/photos/${product.photoId}`}
                              alt=""
                              loading="lazy"
                              className="w-8 h-8 rounded-sm object-cover shrink-0 bg-white"
                            />
                          ) : null}
                          <div className="font-semibold text-[0.8125rem] leading-tight line-clamp-3 min-w-0 pe-2">
                            {productName(product)}
                          </div>
                        </div>
                        {/* The price, and beside it the language and the code, or what is left. */}
                        <div
                          className={`flex items-end justify-between gap-1 mt-auto pt-1.5${worded ? " flex-wrap gap-y-0.5" : ""}`}
                        >
                          <div className={worded ? "min-w-0" : "shrink-0"}>
                            {product.notForSale ? (
                              <div className="font-semibold text-[0.6875rem] leading-tight text-[#8c9097] dark:text-white/50">
                                <i className="ri-forbid-line me-0.5" />
                                Not available for sale
                              </div>
                            ) : (
                              <div
                                className={`font-semibold leading-tight ${
                                  product.priceCents === 0 ? "text-[0.75rem] text-success" : "text-[0.938rem] whitespace-nowrap"
                                }`}
                              >
                                {product.priceCents === 0 ? (
                                  // Priced at nothing on purpose (D, 2 October): received free,
                                  // given free. It goes in the basket so the giving is recorded.
                                  <>
                                    <i className="ri-gift-line me-0.5" />
                                    Complimentary item
                                  </>
                                ) : product.priceCents !== null ? (
                                  formatMoney(product.priceCents)
                                ) : product.tracksStock ? (
                                  "No price"
                                ) : (
                                  "Enter price"
                                )}
                              </div>
                            )}
                            {showCost && product.costCents && !product.notForSale ? (
                              <div className="text-[0.625rem] text-[#8c9097] dark:text-white/50 whitespace-nowrap">
                                Cost {formatMoney(product.costCents)}
                              </div>
                            ) : null}
                          </div>
                          <div
                            className={`flex items-center justify-end gap-1 min-w-0 text-[0.625rem] text-[#8c9097] dark:text-white/50${
                              worded ? " ms-auto" : ""
                            }`}
                          >
                            {product.language && product.language.toUpperCase() !== "EN" ? (
                              <span className="badge bg-secondary/10 text-secondary !py-0 !px-1 !mb-0 shrink-0">
                                {product.language.toUpperCase()}
                              </span>
                            ) : null}
                            {product.tracksStock ? (
                              <span
                                className={`badge !py-0.5 !px-1.5 !mb-0 shrink-0 ${
                                  left > 0 ? "bg-light text-default" : "bg-danger/10 text-danger"
                                }`}
                              >
                                {left} left
                              </span>
                            ) : (
                              <span className="truncate">{product.setName || product.code}</span>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )
            ) : (
              <>
              {/*
                A slab that is not on the system yet, which at a show is most of them: the
                game's own Slab, first on the tab, sold by photographing it and typing what
                it went for. The slabs the system does know about follow.
              */}
              {slabSellers.length > 0 && !term ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                  {slabSellers.map((product) => (
                    <button
                      key={product.id}
                      type="button"
                      className="ti-btn ti-btn-light !m-0 !p-3 !h-auto !block text-start !rounded-md border border-dashed border-primary/40"
                      onClick={() => addProduct(product)}
                    >
                      <div className="flex items-center gap-2">
                        <i className="ri-camera-line text-primary text-[1.5rem]" />
                        <div className="min-w-0">
                          <div className="font-semibold text-[0.875rem]">Sell unlisted graded card</div>
                          <div className="text-[0.75rem] text-[#8c9097] dark:text-white/50">
                            {product.game ?? "Other"}: add a photo and enter the price
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : null}
              {slabs.length === 0 ? (
                slabSellers.length > 0 && !term ? null : (
                  <Empty icon="ri-shield-star-line">
                    {term ? "No graded card matches that certificate number or name." : "No company graded cards in inventory."}
                  </Empty>
                )
              ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {slabs.map((slab) => {
                  const chosen = basket.some((line) => line.slabLotId === slab.lotId);
                  return (
                    <button
                      key={slab.lotId}
                      type="button"
                      disabled={chosen}
                      className="ti-btn ti-btn-light !m-0 !p-3 !h-auto !block text-start !rounded-md"
                      onClick={() => addSlab(slab)}
                    >
                      <div className="flex justify-between gap-3">
                        {slab.photoId ? <SlabThumb photoId={slab.photoId} cover={slab.certCover ?? null} /> : null}
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-[0.938rem]">{slab.cardName ?? "Graded card"}</div>
                          <div className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-1">
                            {[slab.grader && slab.grade ? `${slab.grader} ${slab.grade}` : null, slab.certNo ? `Certificate ${slab.certNo}` : "No certificate number yet", slab.location]
                              .filter(Boolean)
                              .join(" · ")}
                          </div>
                        </div>
                        <div className="text-end">
                          <div className="font-semibold text-[1.125rem]">{slab.priceCents === null ? "No price" : formatMoney(slab.priceCents)}</div>
                          {chosen ? <Badge tone="primary">In the basket</Badge> : slab.priceSetOn ? (
                            <div className="text-[0.6875rem] text-[#8c9097] dark:text-white/50">Price last updated {ageOf(slab.priceSetOn)}</div>
                          ) : null}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              )}
              </>
            )}
          </div>
          <div className="px-4 py-2 border-t border-defaultborder text-[0.6875rem] text-[#8c9097] dark:text-white/50 shrink-0">
            {tab === "products" ? "Press and hold a product to pin it to the top of its group. " : ""}
            {tab === "products" && !term ? "Swipe left or right to change category. " : ""}
            Prices as of {new Date(catalog.fetchedAt).toLocaleTimeString("en-SG", { hour: "2-digit", minute: "2-digit" })}.
          </div>
        </section>

        {/* The basket. */}
        <aside className="col-span-12 lg:col-span-4 box !mb-0 flex flex-col min-h-0">
          <div className="box-header !py-3 justify-between">
            <div className="box-title">
              Basket
              <span className="badge bg-light text-default rounded-full ms-1 text-[0.75rem] align-middle">
                {basket.reduce((sum, line) => sum + line.quantity, 0)}
              </span>
            </div>
            {basket.length ? (
              <button
                type="button"
                className="ti-btn ti-btn-light !py-1 !px-2 !text-[0.75rem] !mb-0"
                onClick={() => {
                  setBasket([]);
                  setCorrecting(undefined);
                }}
              >
                Clear basket
              </button>
            ) : null}
          </div>
          {correcting ? (
            <div className="px-4 pt-3">
              <Alert tone="warning">
                <span className="font-semibold">Correcting {correcting.sale.reference}.</span> Correct the basket and complete
                checkout again. {correcting.sale.reference} is reversed automatically when this sale is completed, recorded as
                corrected by {correcting.by.name}.
                <button
                  type="button"
                  className="ti-btn ti-btn-light !py-1 !px-2 !text-[0.75rem] !mb-0 ms-2"
                  onClick={() => {
                    setBasket([]);
                    setCorrecting(undefined);
                  }}
                >
                  Cancel correction
                </button>
              </Alert>
            </div>
          ) : null}
          <div className="box-body flex-1 overflow-y-auto !p-0">
            {done ? (
              <div className="p-4">
                <Alert tone="success" onClose={() => setDone(undefined)}>
                  {done}
                </Alert>
              </div>
            ) : null}
            {trouble ? (
              <div className="p-4">
                <Alert tone="warning" onClose={() => setTrouble(undefined)}>
                  {trouble}
                </Alert>
              </div>
            ) : null}
            {basket.length === 0 && !done ? (
              <Empty icon="ri-shopping-basket-2-line">Tap a product to add it to the basket.</Empty>
            ) : (
              <ul className="list-none mb-0">
                {basket.map((line) => (
                  <BasketRow
                    key={line.lineId}
                    line={line}
                    floor={floorOf(line)}
                    picture={pictureOf(line)}
                    showCost={showCost}
                    onChange={changeLine}
                    onView={(title, urls) => setViewing({ title, urls })}
                  />
                ))}
              </ul>
            )}
          </div>
          <div className="border-t border-defaultborder p-4 shrink-0">
            <div className="flex items-baseline justify-between mb-3">
              <span className="text-[#8c9097] dark:text-white/50">Total</span>
              <span className="font-semibold text-[1.75rem]">{formatMoney(total)}</span>
            </div>
            {basket.length > 0 && total === 0 ? (
              <button
                type="button"
                className="ti-btn ti-btn-success-full !m-0 !py-4 !text-[0.938rem] leading-tight w-full"
                onClick={() => setCheckout("cash")}
              >
                <i className="ri-gift-line text-[1.25rem]" /> Complete complimentary sale
              </button>
            ) : (
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                className="ti-btn ti-btn-success-full !m-0 !py-4 !px-2 !text-[0.938rem] leading-tight"
                disabled={basket.length === 0}
                onClick={() => setCheckout("cash")}
              >
                <i className="ri-money-dollar-circle-line text-[1.25rem]" /> Pay by cash
              </button>
              <button
                type="button"
                className="ti-btn ti-btn-primary-full !m-0 !py-4 !px-2 !text-[0.938rem] leading-tight"
                disabled={basket.length === 0}
                onClick={() => setCheckout("paynow")}
              >
                <i className="ri-qr-code-line text-[1.25rem]" /> Pay by PayNow
              </button>
              {/* Part cash, part PayNow, as one sale (D, 18 October 2026). */}
              <button
                type="button"
                className="ti-btn ti-btn-warning-full !m-0 !py-4 !px-2 !text-[0.938rem] leading-tight"
                disabled={basket.length === 0}
                onClick={() => setCheckout("both")}
              >
                <i className="ri-exchange-dollar-line text-[1.25rem]" /> PayNow and cash
              </button>
            </div>
            )}
          </div>
        </aside>
      </main>

      {viewing ? (
        <Modal open size="lg" title={viewing.title} onClose={() => setViewing(undefined)}>
          <div className="grid grid-cols-12 gap-3">
            {viewing.urls.map((url) => (
              <div key={url.slice(-48)} className={viewing.urls.length > 1 ? "sm:col-span-6 col-span-12" : "col-span-12"}>
                <img src={url} alt={viewing.title} className="w-full max-h-[70vh] object-contain rounded-sm bg-light" />
              </div>
            ))}
          </div>
        </Modal>
      ) : null}

      {checkout ? (
        <PosCheckout
          catalog={catalog}
          method={checkout === "both" ? "paynow" : checkout}
          split={checkout === "both"}
          lines={basket}
          seller={seller}
          locationId={setup.locationId || null}
          event={eventNow}
          {...(correcting ? { corrects: correcting } : {})}
          reserved={reserved}
          onReserve={setReserved}
          earlier={earlier}
          onClose={() => setCheckout(undefined)}
          onDone={(message) => {
            setCheckout(undefined);
            setReserved(undefined);
            if (earlier) setEarlier({ ...earlier, time: nextMinute(earlier.time) });
            setBasket([]);
            setCorrecting(undefined);
            setDone(message);
            void loadCatalog().then((result) => result.catalog && setCatalog(result.catalog));
          }}
        />
      ) : null}

      {showSales ? (
        <PosSales
          catalog={catalog}
          seller={seller}
          setup={setup}
          onSetup={setSetup}
          canVoid={catalog.staff.find((member) => member.id === seller.id)?.canVoid ?? false}
          canSetAccounts={canSetAccounts}
          seesProfit={can(session.user.role, Permission.ProfitView)}
          sellOnly={sellOnly}
          onReload={reloadCatalog}
          onCorrect={startCorrecting}
          onClose={() => setShowSales(false)}
        />
      ) : null}

      {typing ? (
        <PosAmount
          name={typing.name}
          floor={typing.costCents ? { cents: typing.costCents, note: typing.costNote ?? null } : null}
          showCost={showCost}
          onClose={() => setTyping(undefined)}
          onDone={(cents, count, said, photos) => addTyped(typing, cents, count, said, photos)}
        />
      ) : null}

      {buying ? (
        <PosBuy
          catalog={catalog}
          buyer={seller}
          locationId={setup.locationId || null}
          event={eventNow}
          onClose={() => setBuying(false)}
          onDone={(message) => {
            setBuying(false);
            setDone(message);
          }}
        />
      ) : null}

      {counting ? (
        <PosCount
          catalog={catalog}
          counter={seller}
          locationId={setup.locationId || null}
          event={eventNow}
          onClose={() => setCounting(false)}
          onDone={(message) => {
            setCounting(false);
            setDone(message);
            void loadCatalog().then((result) => result.catalog && setCatalog(result.catalog));
          }}
        />
      ) : null}

      {switching ? (
        <PosStaff
          catalog={catalog}
          title="Select salesperson"
          currentId={seller.id}
          onChosen={(person) => chooseSeller(person.id)}
          onClose={() => setSwitching(false)}
        />
      ) : null}
    </div>
  );
}

/**
 * One line of the basket: how many, what one costs, and what that comes to.
 *
 * The price of one is a button on purpose. A standard price is a starting point at a
 * show: fifty packs go at twelve thirty rather than twelve fifty, and the person at the
 * table has to be able to say so in one tap without leaving the basket. What the price
 * used to be stays beside it, so a discount given is never invisible to the desk.
 * Swiping the row to the left takes it out.
 */
function BasketRow({
  line,
  floor,
  picture,
  showCost,
  onChange,
  onView,
}: {
  readonly line: BasketLine;
  /** Open the photos large, to be sure it is the right thing before the money is taken. */
  readonly onView: (title: string, urls: readonly string[]) => void;
  /** The product's picture, small, beside its name. */
  readonly picture: string | null;
  /** What the shop pays for it, and how that was worked out. Absent when nobody has said. */
  readonly floor: { cents: number; note: string | null } | null;
  /** Whether the cost is written out. Under it, the price still turns red either way. */
  readonly showCost: boolean;
  readonly onChange: (lineId: string, change: Partial<Pick<BasketLine, "quantity" | "unitPriceCents" | "note" | "description">>) => void;
}): React.ReactElement {
  const [editing, setEditing] = useState(false);
  /**
   * The words typed for an item not on the system, being put right (D, 15 October 2026: "after
   * I add slab or singles into basket, can you allow me to edit name, because I wrote wrongly
   * the cert number"). Only a typed item has words of its own; a product's name is the product's.
   */
  const [renaming, setRenaming] = useState<string | undefined>();
  const typedNote = line.note ?? null;
  const productPart = typedNote !== null && line.description.endsWith(` (${typedNote})`) ? line.description.slice(0, -(typedNote.length + 3)) : null;
  const [price, setPrice] = useState((line.unitPriceCents / 100).toFixed(2));
  /** The keypad for how many, for a line that is not one of a kind. */
  const [counting, setCounting] = useState(false);
  const [shift, setShift] = useState(0);
  const start = useRef<number | null>(null);

  const discounted = line.listPriceCents !== null && line.unitPriceCents !== line.listPriceCents;
  // Only with the cost shown. While OHC sell at cost the list price is the cost, so "was"
  // and a red price would tell the customer across the table what it cost: D, 30 September.
  const underCost = showCost && floor !== null && line.unitPriceCents < floor.cents;
  // What was photographed at the table comes first; the product's own picture otherwise.
  const taken = (line.photos ?? []).map((photo) => `data:${photo.contentType};base64,${photo.data}`);
  const shown = taken[0] ?? (picture ? `/api/photos/${picture}` : null);

  return (
    <li className="relative overflow-hidden border-b border-defaultborder">
      <div className="absolute inset-0 bg-danger flex items-center justify-end pe-5 text-white font-semibold">Remove</div>
      <div
        className="relative bg-white dark:bg-bodybg2 flex flex-col gap-2 px-4 py-3 transition-transform"
        style={{ transform: `translateX(${shift}px)` }}
        onPointerDown={(event) => {
          start.current = event.clientX;
        }}
        onPointerMove={(event) => {
          if (start.current === null) return;
          setShift(Math.min(0, event.clientX - start.current));
        }}
        onPointerUp={() => {
          start.current = null;
          if (shift < -110) onChange(line.lineId, { quantity: 0 });
          setShift(0);
        }}
        onPointerCancel={() => {
          start.current = null;
          setShift(0);
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex items-start gap-2">
            {shown ? (
              <button
                type="button"
                aria-label="See the photo"
                className="relative shrink-0"
                onClick={() => onView(line.description, taken.length > 0 ? taken : [shown])}
              >
                <img src={shown} alt="" className="w-11 h-11 rounded-sm object-cover border border-defaultborder" />
                {taken.length > 1 ? (
                  <span className="badge bg-primary text-white absolute -top-1.5 -end-1.5 !px-1 !py-0 text-[0.625rem]">
                    {taken.length}
                  </span>
                ) : null}
              </button>
            ) : null}
            <div className="min-w-0">
            {renaming !== undefined && productPart !== null ? (
              <form
                className="flex gap-1 mb-1"
                onSubmit={(event) => {
                  event.preventDefault();
                  const words = renaming.replace(/\s+/g, " ").trim().slice(0, 120);
                  if (words) onChange(line.lineId, { note: words, description: `${productPart} (${words})` });
                  setRenaming(undefined);
                }}
              >
                <input
                  type="text"
                  aria-label="What the item is"
                  className="form-control !py-1 !text-[0.8125rem]"
                  value={renaming}
                  maxLength={120}
                  autoFocus
                  onPointerDown={(event) => event.stopPropagation()}
                  onChange={(event) => setRenaming(event.target.value)}
                />
                <button type="submit" className="ti-btn ti-btn-primary !py-1 !px-2 !text-[0.75rem] !mb-0">
                  Save
                </button>
              </form>
            ) : (
              <div className="font-semibold text-[0.875rem] leading-snug line-clamp-2">
                {line.description}
                {productPart !== null ? (
                  <button
                    type="button"
                    aria-label="Change what was typed for this item"
                    title="Change the name"
                    className="ms-1 text-primary align-middle"
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={() => setRenaming(typedNote ?? "")}
                  >
                    <i className="ri-pencil-line" />
                  </button>
                ) : null}
              </div>
            )}
            {discounted && showCost ? (
              <div className="text-[0.6875rem] text-warning mt-0.5">Original unit price {formatMoney(line.listPriceCents ?? 0)}</div>
            ) : null}
            {floor && showCost ? (
              <div
                className={`text-[0.6875rem] mt-0.5 ${
                  line.unitPriceCents < floor.cents ? "text-danger font-semibold" : "text-[#8c9097] dark:text-white/50"
                }`}
              >
                {line.unitPriceCents < floor.cents ? "Below cost: " : "Cost "}
                {formatMoney(floor.cents)}
              </div>
            ) : null}
            </div>
          </div>
          <div className={`font-semibold shrink-0${line.unitPriceCents === 0 ? " text-success" : ""}`}>
            {line.unitPriceCents === 0 ? "No charge" : formatMoney(line.quantity * line.unitPriceCents)}
          </div>
        </div>

        {/* Wraps when the longer labels (Apply price, Make complimentary) do not fit beside the steppers. */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          {editing ? (
            <form
              className="flex flex-wrap gap-2 mt-1"
              onSubmit={(event) => {
                event.preventDefault();
                const cents = Math.round(Number(price) * 100);
                if (Number.isFinite(cents) && cents >= 0) onChange(line.lineId, { unitPriceCents: cents });
                setEditing(false);
              }}
            >
              <input
                id={`price-${line.lineId}`}
                type="text"
                inputMode="decimal"
                aria-label="Price each"
                className="form-control field-short !py-1"
                value={price}
                autoFocus
                onChange={(event) => setPrice(event.target.value)}
              />
              <button type="submit" className="ti-btn ti-btn-primary !py-1 !px-2 !text-[0.75rem] !mb-0 whitespace-nowrap">
                Apply price
              </button>
              {/* A promotion gift (D, 3 October): one tap makes the line a complimentary item, recorded at nothing. */}
              <button
                type="button"
                className="ti-btn ti-btn-success !py-1 !px-2 !text-[0.75rem] !mb-0 whitespace-nowrap"
                onClick={() => {
                  onChange(line.lineId, { unitPriceCents: 0 });
                  setEditing(false);
                }}
              >
                <i className="ri-gift-line" /> Make complimentary
              </button>
            </form>
          ) : (
            <button
              type="button"
              aria-label="Change the price of one"
              title="Unit price"
              className={`inline-flex items-center gap-1 whitespace-nowrap rounded-md border px-2 py-1 font-semibold ${
                line.unitPriceCents === 0 ? "text-[0.75rem]" : "text-[0.813rem]"
              } ${
                underCost
                  ? "border-danger/40 bg-danger/10 text-danger"
                  : discounted
                    ? "border-warning/40 bg-warning/10 text-warning"
                    : "border-primary/30 bg-primary/5 text-primary"
              }`}
              onClick={() => {
                setPrice((line.unitPriceCents / 100).toFixed(2));
                setEditing(true);
              }}
            >
              <i className={underCost ? "ri-error-warning-line" : line.unitPriceCents === 0 ? "ri-gift-line" : "ri-pencil-line"} />
              {line.unitPriceCents === 0 ? "Complimentary item" : `${formatMoney(line.unitPriceCents)} each`}
            </button>
          )}
          <div className="input-group !flex-nowrap !w-auto shrink-0">
          <button
            type="button"
            aria-label="One fewer"
            className="ti-btn ti-btn-icon ti-btn-light !mb-0 !rounded-e-none"
            onClick={() => onChange(line.lineId, { quantity: line.quantity - 1 })}
          >
            <i className="ri-subtract-line" />
          </button>
          <button
            type="button"
            aria-label="Type how many"
            className="form-control field-tiny !py-2 !rounded-none font-semibold"
            disabled={line.limit !== null}
            onClick={() => setCounting(true)}
          >
            {line.quantity}
          </button>
          <button
            type="button"
            aria-label="One more"
            className="ti-btn ti-btn-icon ti-btn-light !mb-0 !rounded-s-none"
            disabled={line.limit !== null && line.quantity >= line.limit}
            onClick={() => onChange(line.lineId, { quantity: line.quantity + 1 })}
          >
              <i className="ri-add-line" />
            </button>
          </div>
          {/*
            Packs go in tens (D): 1 to 10, then 20, 30, rather than ten taps on the plus. It
            read 10p, D's own word for ten packs at a time (2 October), and before that +10;
            his revised guide (4 October) calls it Next 10 packs, set small on two lines.
          */}
          {line.limit === null ? (
            <button
              type="button"
              aria-label="Up to the next ten"
              className="ti-btn ti-btn-light !mb-0 !px-2 !py-1 shrink-0 !text-[0.6875rem] leading-tight font-semibold max-w-[4.25rem]"
              onClick={() => onChange(line.lineId, { quantity: Math.floor(line.quantity / 10) * 10 + 10 })}
            >
              Next 10 packs
            </button>
          ) : null}
        </div>

        {/*
          While a price is being changed, what the thing costs and the sum behind it, on a
          line of its own under the row. Wedged between the Apply price button and the steppers it
          was squeezed into a column a few words wide.
        */}
        {editing && floor && showCost ? (
          <div className="rounded-md bg-light dark:bg-bodybg px-3 py-2 text-[0.75rem] text-[#8c9097] dark:text-white/50">
            <span className="font-semibold text-defaulttextcolor">Cost S${formatMoney(floor.cents)} each</span>
            {floor.note ? <span className="block mt-0.5">{workingOf(floor.note, floor.cents)}</span> : null}
          </div>
        ) : null}
      </div>
      {counting ? (
        <PosNumber
          title={line.description}
          label="Quantity"
          start={line.quantity}
          onClose={() => setCounting(false)}
          onDone={(quantity) => {
            setCounting(false);
            onChange(line.lineId, { quantity });
          }}
        />
      ) : null}
    </li>
  );
}

/**
 * The setup code, typed where the person found out the iPad is not a till yet. It used to
 * be only on the sign-in screen, so somebody with their own iPad had to sign out to find
 * it (D, 5 October 2026). Claiming while signed in ties this session to the iPad, so the
 * catalogue loads straight after.
 */
function SetUpHere({ onDone }: { readonly onDone: () => void }): React.ReactElement {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  return (
    <form
      className="text-start mt-2 mb-3"
      onSubmit={(event) => {
        event.preventDefault();
        setError(undefined);
        setBusy(true);
        api
          .claimDevice(code.trim())
          .then(() => onDone())
          .catch((problem: unknown) => {
            setError(problem instanceof ApiError ? problem.message : "The setup code was not accepted");
            setBusy(false);
          });
      }}
    >
      {error ? <Alert tone="danger">{error}</Alert> : null}
      <label htmlFor="pos-setup-code" className="form-label">
        Setup code
      </label>
      <input
        id="pos-setup-code"
        type="text"
        className="form-control form-control-lg text-center !tracking-[0.3em] font-semibold"
        placeholder="XXXXXXXX"
        autoCapitalize="characters"
        autoCorrect="off"
        spellCheck={false}
        value={code}
        onChange={(changed) => setCode(changed.target.value.toUpperCase())}
      />
      <button type="submit" className="ti-btn ti-btn-primary-full w-full !mb-0 mt-3" disabled={busy || code.trim() === ""}>
        {busy ? "Checking" : "Complete device setup"}
      </button>
    </form>
  );
}

/** The minute after this one, HH:MM, stopping at the end of the day. */
function nextMinute(time: string): string {
  const [hour = 12, minute = 0] = time.split(":").map(Number);
  const total = Math.min(23 * 60 + 59, hour * 60 + minute + 1);
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

/** Which earlier day to key in, and from what time. */
function EarlierDayPicker({
  start,
  onClose,
  onPick,
}: {
  readonly start: { day: string; time: string } | undefined;
  readonly onClose: () => void;
  readonly onPick: (picked: { day: string; time: string } | undefined) => void;
}): React.ReactElement {
  const [day, setDay] = useState(start?.day ?? "");
  const [time, setTime] = useState(start?.time ?? "12:00");
  const today = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Singapore" }).format(new Date());
  return (
    <Modal
      open
      title="Key in an earlier day"
      onClose={onClose}
      footer={
        <>
          {start ? (
            <button type="button" className="ti-btn ti-btn-light me-auto" onClick={() => onPick(undefined)}>
              Back to today
            </button>
          ) : null}
          <button type="button" className="ti-btn ti-btn-light" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="ti-btn ti-btn-primary-full" disabled={!day || day >= today} onClick={() => onPick({ day, time: time || "12:00" })}>
            Start
          </button>
        </>
      }
    >
      <p className="text-[#8c9097] dark:text-white/50 text-[0.813rem]">
        For sales that were not keyed in on the day, from the photos taken at the time. Sell as usual: each sale is recorded on this
        day, a minute after the one before, and the PayNow ones are matched to the OCBC statement in Bank reconciliation afterwards.
        This needs a connection.
      </p>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="earlier-pos-day" className="form-label">Day</label>
          <input id="earlier-pos-day" type="date" className="form-control" value={day} max={today} onChange={(event) => setDay(event.target.value)} />
        </div>
        <div>
          <label htmlFor="earlier-pos-time" className="form-label">First sale at</label>
          <input id="earlier-pos-time" type="time" className="form-control" value={time} onChange={(event) => setTime(event.target.value)} />
        </div>
      </div>
    </Modal>
  );
}

/** A slab's photo on its tile, its cert number covered when the slab says so. */
function SlabThumb({
  photoId,
  cover,
}: {
  readonly photoId: string;
  readonly cover: { readonly left: number; readonly top: number; readonly right: number; readonly bottom: number } | null;
}): React.ReactElement {
  const src = useCoveredPhoto(`/api/photos/${photoId}`, cover);
  return src ? (
    <img src={src} alt="" loading="lazy" className="w-14 h-20 object-cover rounded-sm border border-defaultborder shrink-0" />
  ) : (
    <span className="w-14 h-20 rounded-sm border border-defaultborder bg-light shrink-0" />
  );
}
